import React, { useState, useEffect, useRef } from 'react';

const getFeaturedVideo = async () => {
  try {
    const response = await fetch('/internal/video/featured/');
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return await response.json();
  } catch (error) {
    console.error('Error fetching featured video:', error);
    return null;
  }
};

const getAllVideos = async () => {
  try {
    const response = await fetch('/internal/videos/');
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    const data = await response.json();
    return data.results || [];
  } catch (error) {
    console.error('Error fetching all videos:', error);
    return [];
  }
};

const FALLBACK_VIDEO = {
  title: 'Defense Technology Trends 2025',
  youtube_id: 'dQw4w9WgXcQ',
  thumbnail_url: 'https://img.youtube.com/vi/dQw4w9WgXcQ/maxresdefault.jpg',
  channel_name: '',
  duration: '',
  published_date: null,
  summary_points: [
    'AI integration in military systems reaching critical mass',
    'Autonomous weapons platforms showing promising battlefield results',
    'Cyber warfare capabilities expanding across all domains',
  ],
};

// ── Icons ─────────────────────────────────────────────────────────────────────

const PlayIcon = ({ size = 28 }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" width={size} height={size}>
    <path d="M8 5v14l11-7z" />
  </svg>
);

const CloseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
    strokeLinecap="round" width="18" height="18">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
    strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const ExternalIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" width="13" height="13">
    <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6" />
    <polyline points="15 3 21 3 21 9" />
    <line x1="10" y1="14" x2="21" y2="3" />
  </svg>
);

// ── Related Video Card ────────────────────────────────────────────────────────

const RelatedCard = ({ v, onSelect }) => {
  const [hovered, setHovered] = useState(false);
  const [thumbErr, setThumbErr] = useState(false);

  const thumb = thumbErr
    ? `https://img.youtube.com/vi/${v.youtube_id}/hqdefault.jpg`
    : v.thumbnail_url;

  return (
    <div
      style={{
        ...s.relatedCard,
        transform: hovered ? 'translateY(-3px)' : 'translateY(0)',
        boxShadow: hovered
          ? '0 12px 32px rgba(0,0,0,0.5), 0 0 0 1px rgba(59,130,246,0.3)'
          : '0 4px 16px rgba(0,0,0,0.3)',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => onSelect(v)}
      role="button"
      tabIndex={0}
      aria-label={`Play ${v.title}`}
      onKeyDown={e => e.key === 'Enter' && onSelect(v)}
    >
      <div style={s.relatedThumbWrap}>
        <img src={thumb} alt={v.title} style={s.relatedThumb} onError={() => setThumbErr(true)} />
        <div style={{ ...s.relatedThumbOverlay, opacity: hovered ? 1 : 0 }} />
        <div style={{
          ...s.relatedPlayBtn,
          opacity: hovered ? 1 : 0,
          transform: hovered ? 'translate(-50%,-50%) scale(1)' : 'translate(-50%,-50%) scale(0.7)',
        }}>
          <PlayIcon size={18} />
        </div>
        {v.duration && <span style={s.relatedBadge}>{v.duration}</span>}
      </div>
      <div style={s.relatedInfo}>
        <p style={s.relatedCardTitle}>{v.title}</p>
        {v.channel_name && <p style={s.relatedChannel}>{v.channel_name}</p>}
        {v.published_date && (
          <p style={s.relatedDate}>
            {new Date(v.published_date).toLocaleDateString('en-US', {
              month: 'short', day: 'numeric', year: 'numeric',
            })}
          </p>
        )}
      </div>
    </div>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────

const VideoSection = () => {
  const [video, setVideo]                 = useState(null);
  const [relatedVideos, setRelatedVideos] = useState([]);
  const [loading, setLoading]             = useState(true);
  const [showEmbed, setShowEmbed]         = useState(false);
  const [imgError, setImgError]           = useState(false);
  const [visiblePoints, setVisiblePoints] = useState([]);
  const [playHover, setPlayHover]         = useState(false);
  const sectionRef   = useRef(null);
  const pointsTimers = useRef([]);

  const normalise = raw => ({
    id:             raw.id             || null,
    title:          raw.title          || '',
    youtube_id:     raw.youtube_id     || '',
    embed_url:      raw.embed_url      || `https://www.youtube-nocookie.com/embed/${raw.youtube_id}`,
    thumbnail_url:  raw.thumbnail_url  || `https://img.youtube.com/vi/${raw.youtube_id}/maxresdefault.jpg`,
    channel_name:   raw.channel_name   || '',
    duration:       raw.duration       || '',
    published_date: raw.published_date || null,
    summary_points: raw.summary_points || [],
  });

  // ── Load featured video ──────────────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      // ── SSR path 1 — video injected into __INITIAL_DATA__ ──
      if (window.__INITIAL_DATA__?.video) {
        setVideo(normalise(window.__INITIAL_DATA__.video));
        delete window.__INITIAL_DATA__.video;
        setLoading(false);
        return;
      }

      // ── SSR path 2 — legacy __VIDEO_OF_WEEK__ key ──
      if (window.__VIDEO_OF_WEEK__?.youtube_id) {
        setVideo(normalise(window.__VIDEO_OF_WEEK__));
        delete window.__VIDEO_OF_WEEK__;
        setLoading(false);
        return;
      }

      // ── Fallback — fetch from internal endpoint ──
      const data = await getFeaturedVideo();
      setVideo(data ? normalise(data) : FALLBACK_VIDEO);
      setLoading(false);
    };
    load();
  }, []);

  // ── Load related videos once featured video is known ────────────────────
  useEffect(() => {
    if (!video) return;
    const loadRelated = async () => {
      const all = await getAllVideos();
      const others = (Array.isArray(all) ? all : [])
        .filter(v => v.youtube_id !== video.youtube_id)
        .map(normalise);
      setRelatedVideos(others);
    };
    loadRelated();
  }, [video]);

  // ── Animate key-takeaway points ─────────────────────────────────────────
  useEffect(() => {
    if (!video) return;
    setVisiblePoints([]);
    pointsTimers.current.forEach(clearTimeout);
    pointsTimers.current = (video.summary_points || []).map((_, i) =>
      setTimeout(() => setVisiblePoints(prev => [...prev, i]), 350 + i * 180)
    );
    return () => pointsTimers.current.forEach(clearTimeout);
  }, [video]);

  // ── Escape key closes embed ──────────────────────────────────────────────
  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') setShowEmbed(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const formatDate = iso => {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
    });
  };

  const handlePlay = () => {
    setShowEmbed(true);
    setTimeout(() => {
      sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };

  const handleSelectRelated = (relatedVideo) => {
    setShowEmbed(false);
    setImgError(false);
    setVisiblePoints([]);
    setRelatedVideos(prev => {
      const withoutSelected = prev.filter(v => v.youtube_id !== relatedVideo.youtube_id);
      if (video && video.youtube_id !== FALLBACK_VIDEO.youtube_id) {
        return [video, ...withoutSelected];
      }
      return withoutSelected;
    });
    setVideo(relatedVideo);
    setTimeout(() => {
      sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };

  // ── Loading skeleton ─────────────────────────────────────────────────────
  if (loading) {
    return (
      <section style={s.section} id="video-section">
        <div style={s.inner}>
          <div style={{ height: 32, width: 260, background: 'rgba(255,255,255,0.07)', borderRadius: 8, marginBottom: '2rem' }} />
          <div style={s.grid}>
            <div style={{ height: 380, borderRadius: 16, background: 'rgba(255,255,255,0.06)' }} />
            <div style={{ ...s.panel, padding: '2rem', display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ height: 28, width: '80%', background: 'rgba(255,255,255,0.07)', borderRadius: 6 }} />
              {[1, 2, 3].map(i => (
                <div key={i} style={{ height: 64, background: 'rgba(255,255,255,0.05)', borderRadius: 10 }} />
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  const V        = video || FALLBACK_VIDEO;
  const sep      = V.embed_url?.includes('?') ? '&' : '?';
  const embedSrc = `${V.embed_url}${sep}autoplay=1&rel=0&modestbranding=1`;
  const watchUrl = `https://www.youtube.com/watch?v=${V.youtube_id}`;
  const thumbSrc = imgError
    ? `https://img.youtube.com/vi/${V.youtube_id}/hqdefault.jpg`
    : V.thumbnail_url;

  return (
    <section style={s.section} ref={sectionRef} id="video-section">
      <div style={s.stripe} aria-hidden="true" />
      <div style={s.inner}>

        <div style={{ marginBottom: '2rem' }}>
          <div style={s.pill}><span style={s.dot} />Featured Video</div>
          <h2 style={s.title}>Video of the Week</h2>
        </div>

        <div style={s.grid}>

          {/* LEFT — player */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>

            {!showEmbed ? (
              <div
                style={s.thumbWrap}
                onClick={handlePlay}
                role="button"
                tabIndex={0}
                aria-label={`Play ${V.title}`}
                onKeyDown={e => e.key === 'Enter' && handlePlay()}
                onMouseEnter={() => setPlayHover(true)}
                onMouseLeave={() => setPlayHover(false)}
              >
                <img src={thumbSrc} alt={V.title} style={s.thumbImg} onError={() => setImgError(true)} />
                <div style={{ ...s.thumbGrad, pointerEvents: 'none' }} />
                {V.duration && <span style={{ ...s.badge, pointerEvents: 'none' }}>{V.duration}</span>}
                <div style={{ ...s.playRing, pointerEvents: 'none' }}>
                  <div style={{
                    ...s.playCircle,
                    background: playHover ? '#dc2626' : 'rgba(255,255,255,0.95)',
                    color:      playHover ? '#fff'    : '#1e3a8a',
                    transform:  playHover ? 'scale(1.1)' : 'scale(1)',
                    boxShadow:  playHover
                      ? '0 0 0 14px rgba(220,38,38,0.18)'
                      : '0 4px 20px rgba(0,0,0,0.4)',
                  }}>
                    <PlayIcon />
                  </div>
                </div>
                <div style={{ ...s.thumbMeta, pointerEvents: 'none' }}>
                  {V.channel_name && <span style={s.channel}>{V.channel_name}</span>}
                  {V.published_date && <span style={s.thumbDate}>{formatDate(V.published_date)}</span>}
                </div>
              </div>
            ) : (
              <div style={s.embedWrap}>
                <iframe
                  key={V.youtube_id}
                  src={embedSrc}
                  title={V.title}
                  style={s.iframe}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                />
                <button style={s.closeBtn} onClick={() => setShowEmbed(false)} aria-label="Close video">
                  <CloseIcon /><span>Close</span>
                </button>
              </div>
            )}

            <a href={watchUrl} target="_blank" rel="noopener noreferrer" style={s.ytLink}>
              <span>Watch on YouTube</span><ExternalIcon />
            </a>
          </div>

          {/* RIGHT — info panel */}
          <div style={s.panel}>
            <div style={{ padding: '2rem' }}>
              <h3 style={s.videoTitle}>{V.title}</h3>
              <div style={s.divider} />
              <p style={s.pointsLabel}>Key Takeaways</p>
              <ul style={s.list}>
                {(V.summary_points || []).map((point, i) => (
                  <li key={i} style={{
                    ...s.listItem,
                    opacity:    visiblePoints.includes(i) ? 1 : 0,
                    transform:  visiblePoints.includes(i) ? 'translateY(0)' : 'translateY(10px)',
                    transition: 'opacity 0.4s ease, transform 0.4s ease',
                  }}>
                    <span style={s.checkIcon}><CheckIcon /></span>
                    <span style={s.pointText}>{point}</span>
                  </li>
                ))}
              </ul>
              <button style={s.ctaBtn} onClick={handlePlay}>
                <PlayIcon size={20} />Watch Now
              </button>
            </div>
          </div>

        </div>

        {/* Videos You May Like */}
        {relatedVideos.length > 0 && (
          <div style={s.relatedSection}>
            <div style={s.relatedDivider} />
            <div style={s.relatedHeader}>
              <div style={s.relatedPill}>
                <span style={{ ...s.dot, background: '#f59e0b', boxShadow: '0 0 6px #f59e0b' }} />
                More Videos
              </div>
              <h3 style={s.relatedTitle}>Videos You May Like</h3>
            </div>
            <div style={s.relatedRow}>
              {relatedVideos.map((v, i) => (
                <RelatedCard key={v.youtube_id || i} v={v} onSelect={handleSelectRelated} />
              ))}
            </div>
          </div>
        )}

      </div>
    </section>
  );
};

// ── Styles ────────────────────────────────────────────────────────────────────

const s = {
  section:   { position: 'relative', background: '#0f172a', padding: '3.5rem 2rem', borderBottom: '1px solid #1e293b' },
  stripe:    { position: 'absolute', inset: 0, backgroundImage: `repeating-linear-gradient(-55deg, transparent, transparent 40px, rgba(255,255,255,0.018) 40px, rgba(255,255,255,0.018) 41px)`, pointerEvents: 'none' },
  inner:     { position: 'relative', maxWidth: 1200, margin: '0 auto' },
  pill:      { display: 'inline-flex', alignItems: 'center', gap: 7, background: 'rgba(30,64,175,0.25)', border: '1px solid rgba(59,130,246,0.35)', color: '#93c5fd', fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', padding: '5px 14px', borderRadius: 100, marginBottom: '0.75rem' },
  dot:       { width: 7, height: 7, borderRadius: '50%', background: '#3b82f6', boxShadow: '0 0 6px #3b82f6' },
  title:     { color: '#f1f5f9', fontSize: '1.85rem', fontWeight: 800, letterSpacing: '-0.02em', margin: 0, fontFamily: "'Georgia', 'Times New Roman', serif" },
  grid:      { display: 'grid', gridTemplateColumns: '1.45fr 1fr', gap: '2rem', alignItems: 'start' },
  thumbWrap: { position: 'relative', cursor: 'pointer', height: 380, background: '#1e293b', borderRadius: 16, boxShadow: '0 20px 60px rgba(0,0,0,0.5)', outline: 'none', userSelect: 'none' },
  thumbImg:  { position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', borderRadius: 16, display: 'block' },
  thumbGrad: { position: 'absolute', inset: 0, borderRadius: 16, background: 'linear-gradient(to top, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.05) 50%, transparent 100%)' },
  badge:     { position: 'absolute', top: 14, right: 14, zIndex: 3, background: 'rgba(0,0,0,0.8)', color: '#fff', fontSize: '0.75rem', fontWeight: 700, padding: '3px 9px', borderRadius: 6, letterSpacing: '0.04em' },
  playRing:  { position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 3 },
  playCircle:{ width: 72, height: 72, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background 0.22s, color 0.22s, transform 0.22s, box-shadow 0.22s', paddingLeft: 4 },
  thumbMeta: { position: 'absolute', bottom: 16, left: 16, right: 16, zIndex: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  channel:   { color: '#e2e8f0', fontSize: '0.8rem', fontWeight: 600, background: 'rgba(0,0,0,0.55)', padding: '4px 10px', borderRadius: 6 },
  thumbDate: { color: '#94a3b8', fontSize: '0.75rem', background: 'rgba(0,0,0,0.45)', padding: '4px 10px', borderRadius: 6 },
  embedWrap: { position: 'relative', height: 380, background: '#000', borderRadius: 16, boxShadow: '0 20px 60px rgba(0,0,0,0.5)' },
  iframe:    { position: 'absolute', inset: 0, width: '100%', height: '100%', border: 'none', borderRadius: 16, display: 'block' },
  closeBtn:  { position: 'absolute', top: 12, left: 12, zIndex: 10, display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(0,0,0,0.75)', color: '#fff', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 8, padding: '6px 12px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600, backdropFilter: 'blur(6px)' },
  ytLink:    { display: 'inline-flex', alignItems: 'center', gap: 6, color: '#64748b', fontSize: '0.78rem', fontWeight: 500, textDecoration: 'none', alignSelf: 'flex-start', padding: '4px 0' },
  panel:     { background: 'linear-gradient(160deg, #1e293b 0%, #162032 100%)', borderRadius: 16, border: '1px solid rgba(255,255,255,0.07)', boxShadow: '0 8px 32px rgba(0,0,0,0.3)' },
  videoTitle:{ color: '#f1f5f9', fontSize: '1.25rem', fontWeight: 700, lineHeight: 1.35, margin: '0 0 1.25rem', fontFamily: "'Georgia', 'Times New Roman', serif" },
  divider:   { height: 1, background: 'linear-gradient(to right, #3b82f6, transparent)', marginBottom: '1.25rem', opacity: 0.4 },
  pointsLabel:{ color: '#64748b', fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', margin: '0 0 0.85rem' },
  list:      { listStyle: 'none', padding: 0, margin: '0 0 1.75rem', display: 'flex', flexDirection: 'column', gap: '0.6rem' },
  listItem:  { display: 'flex', alignItems: 'flex-start', gap: '0.75rem', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 10, padding: '0.85rem 1rem' },
  checkIcon: { flexShrink: 0, marginTop: 2, width: 22, height: 22, borderRadius: '50%', background: 'rgba(59,130,246,0.2)', border: '1px solid rgba(59,130,246,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#60a5fa' },
  pointText: { color: '#cbd5e1', fontSize: '0.88rem', lineHeight: 1.5 },
  ctaBtn:    { width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', background: 'linear-gradient(135deg, #1d4ed8, #1e40af)', color: '#fff', border: 'none', borderRadius: 10, padding: '0.85rem', fontSize: '0.95rem', fontWeight: 700, cursor: 'pointer', letterSpacing: '0.02em', boxShadow: '0 4px 16px rgba(29,78,216,0.4)' },
  relatedSection: { marginTop: '2.5rem' },
  relatedDivider: { height: 1, background: 'linear-gradient(to right, rgba(245,158,11,0.4), rgba(59,130,246,0.2), transparent)', marginBottom: '1.75rem' },
  relatedHeader:  { marginBottom: '1.25rem' },
  relatedPill:    { display: 'inline-flex', alignItems: 'center', gap: 7, background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.3)', color: '#fcd34d', fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', padding: '4px 12px', borderRadius: 100, marginBottom: '0.5rem' },
  relatedTitle:   { color: '#f1f5f9', fontSize: '1.2rem', fontWeight: 700, letterSpacing: '-0.01em', margin: 0, fontFamily: "'Georgia', 'Times New Roman', serif" },
  relatedRow:     { display: 'flex', gap: '1rem', overflowX: 'auto', paddingBottom: '0.75rem', scrollbarWidth: 'thin', scrollbarColor: 'rgba(59,130,246,0.4) transparent' },
  relatedCard:    { flex: '0 0 220px', background: 'linear-gradient(160deg, #1e293b 0%, #162032 100%)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 14, overflow: 'hidden', cursor: 'pointer', transition: 'transform 0.22s ease, box-shadow 0.22s ease', outline: 'none', userSelect: 'none' },
  relatedThumbWrap:   { position: 'relative', height: 124, background: '#0f172a', overflow: 'hidden' },
  relatedThumb:       { width: '100%', height: '100%', objectFit: 'cover', display: 'block', transition: 'transform 0.3s ease' },
  relatedThumbOverlay:{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.45)', transition: 'opacity 0.22s ease', pointerEvents: 'none' },
  relatedPlayBtn:     { position: 'absolute', top: '50%', left: '50%', width: 38, height: 38, borderRadius: '50%', background: 'rgba(255,255,255,0.92)', color: '#1e3a8a', display: 'flex', alignItems: 'center', justifyContent: 'center', paddingLeft: 3, transition: 'opacity 0.22s ease, transform 0.22s ease', pointerEvents: 'none' },
  relatedBadge:       { position: 'absolute', bottom: 7, right: 8, background: 'rgba(0,0,0,0.85)', color: '#fff', fontSize: '0.68rem', fontWeight: 700, padding: '2px 7px', borderRadius: 5, letterSpacing: '0.03em', zIndex: 2 },
  relatedInfo:        { padding: '0.75rem 0.9rem 0.85rem' },
  relatedCardTitle:   { color: '#e2e8f0', fontSize: '0.82rem', fontWeight: 600, lineHeight: 1.4, margin: '0 0 0.35rem', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' },
  relatedChannel:     { color: '#64748b', fontSize: '0.72rem', fontWeight: 500, margin: '0 0 0.2rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  relatedDate:        { color: '#475569', fontSize: '0.68rem', margin: 0 },
};

export default VideoSection;